
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  shallowRef,
  toRef,
  watch,
  inject,
} from "vue";
import { IBulkDelete } from "@/interfaces/iPageModel";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import { TranslationService } from "@/core/services/TranslationService";
import { INNER_PAGE_ROWS_PER_PAGE, usePagination } from "@/helpers/pagination";
import { IAddEditFormModel } from "@/interfaces/iAddEditFormModel";
import { FormatMethods } from "@/helpers/data-format";
import { FormApi } from "@/services/api/Form.service";
import { IEventManagements } from "@/interfaces/iEventManagement";
import VZDeleteModal from "@/components/content/VZDeleteModal.vue";
import { getLookups } from "@/services/api/Lookup.service";
import {
  formEventTableBulkDelete,
  formEventTableFilters,
  formEventTableHeaders,
  EVENT_LOOKUP,
} from "../dataTableOptions";
import { useToast } from "vue-toastification";
import * as Yup from "yup";
import { ITabConfig } from "@/interfaces/iTabConfig";
import { EVENTMANAGEMENT_TABS } from "@/views/eventManagement/dataTableOptions";
import { DrawerComponent } from "@/assets/ts/components";
import PreviewLink from "@/components/typography/PreviewLink.vue";
import EventDetailModal from "@/views/eventManagement/modal/EventDetailModal.vue";
import { hasPermission } from "@/core/services/permission.service";
import { Emitter } from "mitt";
import { debounce } from "lodash";
interface ITabsEmit {
  tabs: ITabConfig[];
  title: string;
  icon?: string;
}
type Events = {
  VZTabsUpdate: ITabsEmit;
};
export default defineComponent({
  props: {
    data: { type: Object as PropType<IEventManagements>, required: true },
    openDrawer: { type: Boolean, default: false },
    isActive: { type: Boolean, default: false },
  },
  components: {
    Datatable,
    VZDeleteModal,
    PreviewLink,
    EventDetailModal,
  },
  setup(props) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    let options: { label: any; value: string }[] = [];
    const dataProp = toRef(props, "data");
    const openDrawer = toRef(props, "openDrawer");
    const { page, total } = usePagination();
    const rowsPerPage = ref(INNER_PAGE_ROWS_PER_PAGE);
    const loading = ref(false);
    const showSearch = true;
    const showDetailDialog = ref<any>(false);
    const showDeleteDialog = ref<any>(false);
    const tableData = ref<IEventManagements[]>([]);
    const selectedForAction = ref<Partial<IEventManagements>>({});
    const localPreference = ref({});
    const bulkDelete = ref<IBulkDelete>(formEventTableBulkDelete());
    const tableHeader = ref(formEventTableHeaders());
    const tableFilter = ref(formEventTableFilters());
    const toast = useToast();
    const eventDeleteMessage = t("pages.form.eventDeleteMessage");
    const eventAssignMessage = t("pages.form.eventAssignSuccess");
    let siteOptions: { label: any; value: string }[] = [];
    let formUsers = ref<any>([] as any);
    const Datatable = ref();
    const emitter: Emitter<Events> | undefined = inject("emitter");
    const defaultFilter = ref({ status: "ACTIVE" });

    const format = computed(() => {
      return FormatMethods;
    });
    const tabs = shallowRef<ITabConfig[]>(EVENTMANAGEMENT_TABS());
    const viewName = "formEvent";
    onMounted(async () => {
      // await refreshList();
      loading.value = true;
      await getLookups(EVENT_LOOKUP.url)
        .then((res) => {
          res.map((data) => {
            options.push({
              label: data.text,
              value: data.value,
            });
          });
        })
        .catch((err) => {
          console.error(err);
        });
      loadSites();
    });

    const loadSites = async () => {
      siteOptions = [];
      await getLookups(
        `site/siteForms/${dataProp.value.webId}/sites?page=0&size=1000`
      )
        .then((res: any) => {
          siteOptions = [];
          res.content.map((data: any) => {
            siteOptions.push({
              label: data.name,
              value: data.webId,
            });
          });
          formData.value.model[1].options = siteOptions;
          formData.value.model[2].options = [];
        })
        .catch((err) => {
          return [];
        });
    };

    const onModalShow = () => {
      loadSites();
    };

    const loadUsers = async (siteId) => {
      formUsers.value = [];
      loading.value = true;
      await getLookups(
        `site/userSites/userAndRole?paramSiteIds=${siteId}&formDefinitionId=${dataProp.value.webId}`
      )
        .then((res) => {
          res.map((data) => {
            formUsers.value.push({
              label: data.userRoleName,
              value: data.userRoleId,
              type: data.type,
            });
          });
          // const idsArray = formUsers.value.map((item) => Number(item.value));
          // const idsToRemove = idsArray.filter((value) => {
          //   // Check if the value exists in either roleId or userId of any object in the second array
          //   return tableData.value.some((obj) => {
          //     if (obj.roleId === null) {
          //       return obj.userId == value;
          //     } else {
          //       return obj.roleId == value;
          //     }
          //   });
          // });
          // const filteredFormUsers = formUsers.value.filter(
          //   (item) => !idsToRemove.some((id) => id === parseInt(item.value))
          // );

          formData.value.model[2].options = formUsers.value;
        })
        .catch((err) => {
          return [];
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const updatedSitesValue = async (siteId) => {
      loadUsers(siteId);
    };

    let formData = ref<IAddEditFormModel>({
      page: "form",
      header: "Form Event",
      edit: false,
      data: null,
      validationSchema: Yup.object({
        eventId: Yup.array()
          .nullable()
          .min(1, t("common.event") + " " + t("common.isRequired"))
          .required(t("common.event") + " " + t("common.isRequired")),
        siteId: Yup.string().required(
          t("common.site") + " " + t("common.isRequired")
        ),
        formUser: Yup.string().required(
          t("pages.form.usersRoles") + " " + t("common.isRequired")
        ),
      }),
      label: [{ addHeader: t("pages.form.assignEvent") }],
      model: [
        {
          name: "eventId",
          label: t("common.event"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select event",
          options: options,
          multiSelect: true,
        },
        {
          name: "siteId",
          label: t("common.site"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select branch",
          options: siteOptions,
          multiSelect: false,
          dependentFields: ["formUser"],
          onValueChange: updatedSitesValue,
        },
        {
          name: "formUser",
          label: t("pages.form.usersRoles"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select User/Role",
          options: formUsers,
          multiSelect: false,
        },
      ],
    });
    let permissionModel = ref<any>({
      create: "VYZOR:TEMPLATES:FORMS:CREATE",
      delete: "VYZOR:TEMPLATES:FORMS:DELETE",
      update: "VYZOR:TEMPLATES:FORMS:UPDATE",
    });
    const edit = (item: any) => {
      formData.value.data = { ...item };
      formData.value.edit = true;
    };

    const submit = async (reqData) => {
      try {
        const data = reqData.eventId.map((item) => {
          return {
            eventId: item,
            siteId: reqData.siteId,
            formDefinitionId: dataProp.value.webId,
            userId:
              formUsers.value.find((user) => user.value == reqData?.formUser)
                ?.type == "USER"
                ? reqData?.formUser
                : null,
            roleId:
              formUsers.value.find((user) => user.value == reqData?.formUser)
                ?.type == "ROLE"
                ? reqData?.formUser
                : null,
          };
        });
        loading.value = true;
        await FormApi.getInstance().addFormEvent(data, formData.value.edit);
        toast.success(`${eventAssignMessage}`);
        queryUpdate(dataProp.value.webId, {
          page: page.value - 1,
          size: rowsPerPage.value,
          status: "ACTIVE",
        });
      } catch (error) {
        // toast.error(t("pages.form.assignEventError"));
        // console.log(error);
      } finally {
        loading.value = false;
      }
    };

    const syncQuery = async (params?: Record<string, number | string>) => {
      localPreference.value = { ...defaultFilter.value };
      queryUpdate(dataProp.value.webId, params);
    };

    const queryUpdate = async (
      webId: number,
      params?: Record<string, number | string>
    ) => {
      try {
        loading.value = true;
        if (!params || !params.sort) {
          params = {
            ...params,
            sort: "createdDate,desc",
          };
        }
        const data = await FormApi.getInstance().getFormEventList(
          dataProp.value.webId,
          {
            ...params,
          }
        );
        localPreference.value = { ...params };
        tableData.value = [...data.content];
        total.value = data.totalElements;
        rowsPerPage.value = data.size;
        page.value = ++data.number;
      } finally {
        loading.value = false;
      }
    };

    const onBulkDelete = async (eventIds) => {
      showDeleteDialog.value = false;
      try {
        const deletedItems = eventIds.map((webId) => {
          const foundItem = tableData.value.find(
            (item) => item.webId === webId
          );
          if (foundItem) {
            return {
              siteId: foundItem.siteId,
              eventId: foundItem.eventId,
              userId: foundItem.userId,
              roleId: foundItem.roleId,
            };
          }
          return null;
        });

        const validDeletedItems = deletedItems.filter((item) => item !== null);
        const collection = validDeletedItems.map((item) => {
          return {
            eventId: Number(item.eventId),
            siteId: Number(item.siteId),
            userId: item.userId,
            roleId: item.roleId,
          };
        });
        await FormApi.getInstance().deleteFormEventBulk(
          dataProp.value.webId,
          collection
        );
        toast.success(eventDeleteMessage);
        refreshList();
      } catch (e) {
        // Handle errors
        console.error(e);
      }
    };

    const onspecificDelete = async (eventIds) => {
      let payload = [
        {
          eventId: eventIds[0],
          siteId: eventIds[1],
          roleId: eventIds[2],
          userId: eventIds[3],
        },
      ];
      try {
        await FormApi.getInstance()
          .deleteFormEvent(dataProp.value.webId, payload)
          .then(() => {
            toast.success(eventDeleteMessage);
          })
          .catch((err) => {
            console.error(err);
          });
      } catch (e) {
        //
      } finally {
        // showDeleteDialog.value = false;
        refreshList();
      }
    };
    const refreshList = () => {
      showDeleteDialog.value = false;
      queryUpdate(dataProp.value.webId, { ...localPreference.value });
    };
    const openDetailModal = async (site) => {
      tabs.value = tabs.value.map((tab) => {
        return { ...tab, props: { data: site } };
      });
      emitter?.emit("VZTabsUpdate", {
        tabs: tabs.value,
        title: site.name,
        icon: "bi bi-diagram-3 fs-2 text-primary",
      });
      if (DrawerComponent?.getInstance("vz_detail_drawer")?.isShown()) {
        DrawerComponent?.getInstance("vz_detail_drawer")?.hide();
      }
      DrawerComponent?.getInstance("vz_detail_drawer")?.toggle();
    };

    const webId = computed(() => props.data?.webId);
    let lastFetchedWebId = null;
    const debouncedQueryUpdate = debounce(async (webId) => {
      if (webId !== lastFetchedWebId) {
        tableData.value = [];
        rowsPerPage.value = INNER_PAGE_ROWS_PER_PAGE;
        lastFetchedWebId = webId;
        queryUpdate(+webId, {
          page: page.value - 1,
          size: rowsPerPage.value,
          status: "ACTIVE",
        });
        if (Datatable.value.VZFilter) {
          Datatable.value.VZFilter.getSavedFilterList();
        }
      }
    }, 300);
    watch(
      () => props.isActive,
      async (isActive) => {
        if (isActive && dataProp.value.webId) {
          loading.value = true;
          if (Datatable.value?.VZFilter) {
            await Datatable.value.VZFilter.getAppliedFilters();
          }
        } else {
          tableData.value = [];
        }
      }
    );
    return {
      bulkDelete,
      format,
      tableData,
      tableHeader,
      tableFilter,
      showSearch,
      page,
      total,
      rowsPerPage,
      loading,
      selectedForAction,
      syncQuery,
      refreshList,
      formData,
      submit,
      viewName,
      edit,
      onBulkDelete,
      showDetailDialog,
      openDetailModal,
      t,
      tabs,
      hasPermission,
      permissionModel,
      showDeleteDialog,
      onModalShow,
      onspecificDelete,
      Datatable,
      defaultFilter,
    };
  },
});
